import React, { useContext, useState, useEffect, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/axiosConfig';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import './Reports.css';
import * as XLSX from 'xlsx';
import { generateTramiteExcelRow } from '../utils/exportExcelUtils';

const Reports = () => {
  const { user } = useContext(AuthContext);
  const isSuperUserForReports = user?.role === 'superadmin' || user?.profile_name === 'superadmin' || user?.username === 'laura.avila' || user?.profile_name === 'auxiliar transiapp';
  const [reportType, setReportType] = useState(isSuperUserForReports ? 'cupls' : (user?.transito_id || user?.concesionario_id ? 'tramites' : 'caja'));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    dateType: 'reception', // 'reception' o 'payment'
    status: 'todos',
    type: 'todos',
    zona_id: ''
  });
  const [zonasFilterList, setZonasFilterList] = useState([]);

  useEffect(() => {
    const fetchZonasFilter = async () => {
      try {
        const concId = user?.concesionario_id || '';
        const res = await axios.get(`/api/zonas${concId ? `?concesionario_id=${concId}` : ''}`);
        setZonasFilterList(res.data);
      } catch (err) {
        console.error('Error al cargar zonas para filtro:', err);
      }
    };
    fetchZonasFilter();
  }, [user?.concesionario_id]);

  // Helper para formatear moneda sin decimales y con separador de miles
  const formatMoney = (value) => {
    if (value === null || value === undefined) return '$0';
    return '$' + Math.round(parseFloat(value)).toLocaleString('es-CO');
  };

  // Obtener reportes según tipo
  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let endpoint = '';
      let params = new URLSearchParams();

      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.dateType) params.append('dateType', filters.dateType);
      if (filters.zona_id) params.append('zona_id', filters.zona_id);

      switch (reportType) {
        case 'cupls':
          endpoint = `/api/cupl?${params.toString()}`;
          break;
        case 'pagos':
          params.set('status', 'pagado');
          if (reportType === 'pagos') params.set('dateType', 'payment');
          endpoint = `/api/cupl?${params.toString()}`;
          break;
        case 'caja':
          if (filters.type !== 'todos') params.append('type', filters.type);
          endpoint = `/api/cash/movements?${params.toString()}`;
          break;
        case 'auditoria':
          endpoint = '/api/audit/logs';
          break;
        case 'tramites':
          if (filters.status !== 'todos') params.append('estado', filters.status);
          endpoint = `/api/reports/tramites?${params.toString()}`;
          break;
        default:
          endpoint = '/api/cupl';
      }

      if (!endpoint) {
        setError('Endpoint no disponible');
        setLoading(false);
        return;
      }

      const response = await axios.get(endpoint);
      const result = response.data;
      
      // Validar y asegurar que data tenga el formato correcto
      if (Array.isArray(result)) {
        setData(result);
      } else if (result && typeof result === 'object') {
        console.warn('Se esperaba array pero se recibió objeto:', result);
        setData([]);
      } else {
        setData([]);
      }
    } catch (err) {
      setError('Error al cargar el reporte: ' + err.message);
      console.error(err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [reportType, filters.startDate, filters.endDate, filters.dateType, filters.type, filters.status, filters.zona_id]);

  // Cargar datos al cambiar el tipo de reporte o filtros
  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Exportar a Excel
  const exportToExcel = () => {
    if (data.length === 0) {
      setError('No hay datos para exportar');
      return;
    }

    const workbook = XLSX.utils.book_new();
    let fileName = `Reporte_${reportType}_${new Date().toLocaleDateString('es-CO')}.xlsx`;

    // Formato de miles sin decimales para Excel
    const numFormat = '#,##0';
    const percentFormat = '0.00%';

    if (reportType === 'cupls') {
      // 1. Datos de Detalle
      const exportData = data.map(cupl => ({
        'CUPL': cupl.cupl_number,
        'Placa': cupl.plate,
        'Concesionario': cupl.concesionario_name || 'N/A',
        'Tipo Documento': cupl.document_type || 'N/A',
        'Nro Documento': cupl.document_number || 'N/A',
        'Organismo Tránsito': cupl.transit_organism || 'N/A',
        'Propietario': cupl.owner_name,
        'Trámite': cupl.tramite_name,
        'Valor': Math.round(parseFloat(cupl.tramite_value) || 0),
        'Estado': cupl.is_paid ? 'Pagado' : 'Pendiente',
        'Creado por': cupl.created_by_user,
        'Fecha Creación': new Date(cupl.created_at).toLocaleDateString('es-CO'),
        'Pagado el': cupl.paid_at ? new Date(cupl.paid_at).toLocaleDateString('es-CO') : 'N/A'
      }));

      // 2. Generar Dashboard
      const totalCUPLs = data.length;
      const totalPagados = data.filter(c => c.is_paid).length;
      const totalPendientes = totalCUPLs - totalPagados;
      const montoTotal = Math.round(data.reduce((sum, c) => sum + (parseFloat(c.tramite_value) || 0), 0));
      const montoPagado = Math.round(data.filter(c => c.is_paid).reduce((sum, c) => sum + (parseFloat(c.tramite_value) || 0), 0));
      const montoPendiente = montoTotal - montoPagado;
      const cumplimiento = totalCUPLs > 0 ? (totalPagados / totalCUPLs) : 0;

      // Resumen por Trámite
      const tramiteSummary = Object.values(data.reduce((acc, c) => {
        if (!acc[c.tramite_name]) {
          acc[c.tramite_name] = { 'Trámite': c.tramite_name, 'Total': 0 };
        }
        acc[c.tramite_name].Total += (parseFloat(c.tramite_value) || 0);
        return acc;
      }, {})).map(t => ({ 'Indicador': t.Trámite, 'Valor': Math.round(t.Total) }));

      // Resumen por Usuario (Cantidad)
      const userSummary = Object.values(data.reduce((acc, c) => {
        const user = c.created_by_user || 'Sistema';
        if (!acc[user]) {
          acc[user] = { 'Usuario': user, 'Registros': 0 };
        }
        acc[user].Registros++;
        return acc;
      }, {})).map(u => ({ 'Indicador': u.Usuario, 'Valor': u.Registros }));

      // Construir la hoja de Dashboard manualmente para aplicar formatos
      const dashRows = [
        ['DASHBOARD DE GESTIÓN CUPL'],
        [`Generado el: ${new Date().toLocaleString()}`],
        [],
        ['RESUMEN GENERAL'],
        ['Total CUPLs', totalCUPLs],
        ['CUPLs Pagados', totalPagados],
        ['CUPLs Pendientes', totalPendientes],
        ['% Cumplimiento', cumplimiento],
        [],
        ['RESUMEN FINANCIERO'],
        ['Monto Total', montoTotal],
        ['Monto Recaudado', montoPagado],
        ['Monto Pendiente', montoPendiente],
        [],
        ['DISTRIBUCIÓN POR TRÁMITE'],
        ...tramiteSummary.map(t => [t.Indicador, t.Valor]),
        [],
        ['DESEMPEÑO POR USUARIO (Registros)'],
        ...userSummary.map(u => [u.Indicador, u.Valor])
      ];

      const wsDashboard = XLSX.utils.aoa_to_sheet(dashRows);

      // Aplicar formatos numéricos a la hoja de Dashboard
      // Fila 5-8 son resumen general. Fila 8 es % cumplimiento (index 7)
      // Fila 11-13 son financiero (index 10, 11, 12)
      // Fila 16+ son trámites
      dashRows.forEach((row, i) => {
        const cellRef = XLSX.utils.encode_cell({ r: i, c: 1 });
        if (!wsDashboard[cellRef]) return;

        if (i === 7) { // % Cumplimiento
          wsDashboard[cellRef].z = percentFormat;
        } else if ((i >= 10 && i <= 12) || (i >= 15 && i < 15 + tramiteSummary.length)) {
          // Valores monetarios
          wsDashboard[cellRef].z = numFormat;
        } else if ((i >= 4 && i <= 6) || (i >= 17 + tramiteSummary.length)) {
          // Cantidades
          wsDashboard[cellRef].z = numFormat;
        }
      });

      // Anchos de columna para Dashboard
      wsDashboard['!cols'] = [{ wch: 35 }, { wch: 20 }];

      // Hoja de detalle
      const wsDetail = XLSX.utils.json_to_sheet(exportData);
      
      // Aplicar formato de miles a la columna Valor (columna H, index 7)
      const rangeDetail = XLSX.utils.decode_range(wsDetail['!ref']);
      for (let r = rangeDetail.s.r + 1; r <= rangeDetail.e.r; r++) {
        const cell = wsDetail[XLSX.utils.encode_cell({ r, c: 7 })];
        if (cell) cell.z = numFormat;
      }
      wsDetail['!cols'] = [{ wch: 15 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 25 }, { wch: 25 }, { wch: 15 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];

      XLSX.utils.book_append_sheet(workbook, wsDashboard, '📊 DASHBOARD');
      XLSX.utils.book_append_sheet(workbook, wsDetail, '📋 DETALLE CUPL');

    } else if (reportType === 'pagos') {
      const pagos = data.filter(cupl => cupl.is_paid);
      
      // 1. Datos de Detalle
      const exportData = pagos.map(cupl => ({
        'CUPL': cupl.cupl_number,
        'Placa': cupl.plate,
        'Concesionario': cupl.concesionario_name || 'N/A',
        'Tipo Documento': cupl.document_type || 'N/A',
        'Nro Documento': cupl.document_number || 'N/A',
        'Organismo': cupl.transit_organism || 'N/A',
        'Propietario': cupl.owner_name,
        'Trámite': cupl.tramite_name,
        'Valor Pagado': Math.round(parseFloat(cupl.tramite_value) || 0),
        'Pagado por': cupl.paid_by_user,
        'Fecha Pago': new Date(cupl.paid_at).toLocaleDateString('es-CO'),
      }));

      // 2. Generar Dashboard para Pagos
      const totalPagos = pagos.length;
      const montoRecaudado = Math.round(pagos.reduce((sum, c) => sum + (parseFloat(c.tramite_value) || 0), 0));

      // Resumen por Trámite (Monto)
      const tramiteSummary = Object.values(pagos.reduce((acc, c) => {
        if (!acc[c.tramite_name]) {
          acc[c.tramite_name] = { 'Trámite': c.tramite_name, 'Total': 0 };
        }
        acc[c.tramite_name].Total += (parseFloat(c.tramite_value) || 0);
        return acc;
      }, {})).map(t => ({ 'Indicador': t.Trámite, 'Valor': Math.round(t.Total) }));

      // Resumen por Usuario que recibió el pago
      const userSummary = Object.values(pagos.reduce((acc, c) => {
        const user = c.paid_by_user || 'Sistema';
        if (!acc[user]) {
          acc[user] = { 'Usuario': user, 'Cantidad': 0 };
        }
        acc[user].Cantidad++;
        return acc;
      }, {})).map(u => ({ 'Indicador': u.Usuario, 'Valor': u.Cantidad }));

      const dashRows = [
        ['DASHBOARD DE RECAUDACIÓN (PAGOS REALIZADOS)'],
        [`Generado el: ${new Date().toLocaleString()}`],
        [],
        ['RESUMEN GENERAL'],
        ['Total Pagos Realizados', totalPagos],
        ['Monto Total Recaudado', montoRecaudado],
        [],
        ['DISTRIBUCIÓN DE RECAUDO POR TRÁMITE'],
        ...tramiteSummary.map(t => [t.Indicador, t.Valor]),
        [],
        ['RECAUDO POR CAJERO (Cantidad de Pagos)'],
        ...userSummary.map(u => [u.Indicador, u.Valor])
      ];

      const wsDashboard = XLSX.utils.aoa_to_sheet(dashRows);

      // Aplicar formatos numéricos
      dashRows.forEach((row, i) => {
        const cellRef = XLSX.utils.encode_cell({ r: i, c: 1 });
        if (!wsDashboard[cellRef]) return;

        if (i === 5 || (i >= 8 && i < 8 + tramiteSummary.length)) {
          // Valores monetarios (Monto total y distribución trámites)
          wsDashboard[cellRef].z = numFormat;
        } else if (i === 4 || (i >= 11 + tramiteSummary.length)) {
          // Cantidades (Total pagos y por cajero)
          wsDashboard[cellRef].z = numFormat;
        }
      });

      wsDashboard['!cols'] = [{ wch: 35 }, { wch: 20 }];

      // Hoja de detalle
      const wsDetail = XLSX.utils.json_to_sheet(exportData);
      const range = XLSX.utils.decode_range(wsDetail['!ref']);
      for (let r = range.s.r + 1; r <= range.e.r; r++) {
        const cell = wsDetail[XLSX.utils.encode_cell({ r, c: 7 })]; // Valor Pagado es col H
        if (cell) cell.z = numFormat;
      }
      wsDetail['!cols'] = [
        { wch: 15 }, // CUPL
        { wch: 10 }, // Placa
        { wch: 15 }, // Tipo Doc
        { wch: 15 }, // Nro Doc
        { wch: 25 }, // Organismo
        { wch: 25 }, // Propietario
        { wch: 25 }, // Trámite
        { wch: 15 }, // Valor Pagado
        { wch: 15 }, // Pagado por
        { wch: 15 }  // Fecha Pago
      ];

      XLSX.utils.book_append_sheet(workbook, wsDashboard, '📊 DASHBOARD PAGOS');
      XLSX.utils.book_append_sheet(workbook, wsDetail, '📋 DETALLE PAGOS');
    } else if (reportType === 'caja') {
      const exportData = data.map(mov => ({
        'Tipo': mov.type === 'ingreso' ? 'INGRESO' : 'EGRESO',
        'Descripción': mov.description,
        'Monto': Math.round(parseFloat(mov.amount) || 0),
        'Creado por': mov.created_by_user,
        'Fecha': new Date(mov.created_at).toLocaleDateString('es-CO'),
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let r = range.s.r + 1; r <= range.e.r; r++) {
        const cell = ws[XLSX.utils.encode_cell({ r, c: 2 })]; // Monto es col C
        if (cell) cell.z = numFormat;
      }
      ws['!cols'] = [{ wch: 12 }, { wch: 40 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(workbook, ws, 'Caja');
    } else if (reportType === 'auditoria') {
      const exportData = data.map(log => ({
        'Usuario': log.user_name,
        'Acción': log.action,
        'Tabla': log.table_name,
        'Registro ID': log.record_id,
        'Detalles': log.changes,
        'Fecha': new Date(log.created_at).toLocaleDateString('es-CO'),
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [{ wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 50 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(workbook, ws, 'Auditoria');
    } else if (reportType === 'tramites') {
      const exportData = data.map(t => generateTramiteExcelRow(t, user, filters.status));
      const ws = XLSX.utils.json_to_sheet(exportData);
      // Anchos automáticos o dinámicos
      const colWidths = Object.keys(exportData[0] || {}).map(key => ({
        wch: Math.max(key.length, ...exportData.map(row => String(row[key] ?? '').length)) + 2
      }));
      ws['!cols'] = colWidths;
      XLSX.utils.book_append_sheet(workbook, ws, 'Trámites Generales');
    }

    XLSX.writeFile(workbook, fileName);
  };

  // Renderizar tabla según tipo de reporte
  const renderReportTable = () => {
    if (loading) return <p className="loading">⏳ Cargando reporte...</p>;
    if (error) return <p className="error">{error}</p>;

    switch (reportType) {
      case 'cupls':
        return renderCUPLsTable();
      case 'pagos':
        return renderPagosTable();
      case 'caja':
        return renderCajaTable();
      case 'auditoria':
        return renderAuditoriaTable();
      case 'tramites':
        return renderTramitesTable();
      default:
        return null;
    }
  };

  const renderCUPLsTable = () => {
    // Validar que data sea un array
    if (!Array.isArray(data)) {
      console.error('Data no es un array:', data);
      return <p className="error">Error: datos inválidos recibidos del servidor</p>;
    }

    const filteredData = filters.status === 'todos'
      ? data
      : filters.status === 'pagado'
        ? data.filter(c => c.is_paid)
        : data.filter(c => !c.is_paid);

    if (filteredData.length === 0) return <p className="no-data">No hay datos</p>;

    return (
      <div className="table-responsive">
        <table className="report-table">
        <thead>
          <tr>
            <th>CUPL</th>
            <th>Placa</th>
            <th>Concesionario</th>
            <th>Documento</th>
            <th>Organismo</th>
            <th>Propietario</th>
            <th>Trámite</th>
            <th>Valor</th>
            <th>Estado</th>
            <th>Fecha</th>
          </tr>
        </thead>
        <tbody>
          {filteredData.map(cupl => (
            <tr key={cupl.id} className={cupl.is_paid ? 'paid' : 'pending'}>
              <td>{cupl.cupl_number}</td>
              <td>{cupl.plate}</td>
              <td>{cupl.concesionario_name || 'N/A'}</td>
              <td>
                <small>{cupl.document_type || ''}</small><br/>
                {cupl.document_number || 'N/A'}
              </td>
              <td style={{ maxWidth: '150px', fontSize: '0.85em' }}>{cupl.transit_organism || 'N/A'}</td>
              <td>{cupl.owner_name}</td>
              <td>{cupl.tramite_name}</td>
              <td className="currency">{formatMoney(cupl.tramite_value)}</td>
              <td><span className={`badge ${cupl.is_paid ? 'success' : 'warning'}`}>
                {cupl.is_paid ? '✓ Pagado' : '⏳ Pendiente'}
              </span></td>
              <td>{new Date(cupl.created_at).toLocaleDateString('es-CO')}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    );
  };

  const renderPagosTable = () => {
    // Validar que data sea un array
    if (!Array.isArray(data)) {
      console.error('Data no es un array:', data);
      return <p className="error">Error: datos inválidos recibidos del servidor</p>;
    }

    const pagos = data.filter(c => c.is_paid);
    if (pagos.length === 0) return <p className="no-data">No hay pagos registrados</p>;

    return (
      <div className="table-responsive">
        <table className="report-table">
        <thead>
          <tr>
            <th>CUPL</th>
            <th>Placa</th>
            <th>Concesionario</th>
            <th>Documento</th>
            <th>Organismo</th>
            <th>Propietario</th>
            <th>Trámite</th>
            <th>Valor Pagado</th>
            <th>Pagado por</th>
            <th>Fecha Pago</th>
          </tr>
        </thead>
        <tbody>
          {pagos.map(pago => (
            <tr key={pago.id}>
              <td>{pago.cupl_number}</td>
              <td>{pago.plate}</td>
              <td>{pago.concesionario_name || 'N/A'}</td>
              <td>
                <small>{pago.document_type || ''}</small><br/>
                {pago.document_number || 'N/A'}
              </td>
              <td style={{ maxWidth: '150px', fontSize: '0.85em' }}>{pago.transit_organism || 'N/A'}</td>
              <td>{pago.owner_name}</td>
              <td>{pago.tramite_name}</td>
              <td className="currency">{formatMoney(pago.tramite_value)}</td>
              <td>{pago.paid_by_user}</td>
              <td>{new Date(pago.paid_at).toLocaleDateString('es-CO')}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    );
  };

  const renderCajaTable = () => {
    // Validar que data sea un array
    if (!Array.isArray(data)) {
      console.error('Data no es un array para caja:', data);
      return <p className="error">Error: datos inválidos recibidos del servidor</p>;
    }

    const filteredData = filters.type === 'todos'
      ? data
      : data.filter(m => m.type === filters.type);

    if (filteredData.length === 0) return <p className="no-data">No hay movimientos</p>;

    const totals = {
      ingresos: filteredData
        .filter(m => m.type === 'ingreso')
        .reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0),
      egresos: filteredData
        .filter(m => m.type === 'egreso')
        .reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0),
    };

    const neto = totals.ingresos - totals.egresos;

    return (
      <>
        <div className="totals-box">
          <div className="total-item">
            <span>Total Ingresos:</span>
            <strong className="income">{formatMoney(totals.ingresos)}</strong>
          </div>
          <div className="total-item">
            <span>Total Egresos:</span>
            <strong className="expense">{formatMoney(totals.egresos)}</strong>
          </div>
          <div className="total-item">
            <span>Neto:</span>
            <strong className={neto >= 0 ? 'success' : 'danger'}>
              {formatMoney(neto)}
            </strong>
          </div>
        </div>
        <div className="table-responsive">
          <table className="report-table">
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Descripción</th>
              <th>Monto</th>
              <th>Creado por</th>
              <th>Fecha</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((mov, idx) => (
              <tr key={idx} className={mov.type === 'ingreso' ? 'income-row' : 'expense-row'}>
                <td><span className={`badge ${mov.type === 'ingreso' ? 'success' : 'danger'}`}>
                  {mov.type === 'ingreso' ? '📥 INGRESO' : '📤 EGRESO'}
                </span></td>
                <td>{mov.description}</td>
                <td className="currency">{formatMoney(mov.amount)}</td>
                <td>{mov.created_by_user}</td>
                <td>{new Date(mov.created_at).toLocaleDateString('es-CO')}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </>
    );
  };

  const renderAuditoriaTable = () => {
    // Validar que data sea un array
    if (!Array.isArray(data)) {
      console.error('Data no es un array para auditoría:', data);
      return <p className="no-data">No hay registros de auditoría</p>;
    }

    if (data.length === 0) return <p className="no-data">No hay registros de auditoría</p>;

    return (
      <div className="table-responsive">
        <table className="report-table">
        <thead>
          <tr>
            <th>Usuario</th>
            <th>Acción</th>
            <th>Tabla</th>
            <th>Registro ID</th>
            <th>Detalles</th>
            <th>Fecha</th>
          </tr>
        </thead>
        <tbody>
          {data.map((log, idx) => (
            <tr key={idx}>
              <td>{log.user_name || 'Sistema'}</td>
              <td><span className="action-badge">{log.action || 'N/A'}</span></td>
              <td>{log.table_name || 'N/A'}</td>
              <td>{log.record_id || 'N/A'}</td>
              <td className="changes">{typeof log.changes === 'string' ? log.changes : JSON.stringify(log.changes || {})}</td>
              <td>{log.created_at ? new Date(log.created_at).toLocaleDateString('es-CO') + ' ' + new Date(log.created_at).toLocaleTimeString('es-CO') : 'N/A'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    );
  };

  const renderTramitesTable = () => {
    if (!Array.isArray(data)) return <p className="error">Error: datos inválidos recibidos del servidor</p>;
    if (data.length === 0) return <p className="no-data">No hay trámites registrados</p>;
    
    return (
      <div className="table-responsive">
        <table className="report-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Placa</th>
            <th>Propietario</th>
            <th>Trámite</th>
            <th>Tránsito</th>
            {(!user?.transito_id && user?.role !== 'transito') && <th>Concesionario</th>}
            <th>Zona</th>
            <th>Estado</th>
            <th>Fecha</th>
          </tr>
        </thead>
        <tbody>
          {data.map(t => (
            <tr key={t.id}>
              <td>#{t.id}</td>
              <td>{t.plate}</td>
              <td>{t.propietario}</td>
              <td>{t.tipo_tramite_name}</td>
              <td>{t.transito_name || 'N/A'}</td>
              {(!user?.transito_id && user?.role !== 'transito') && <td>{t.concesionario_name || 'N/A'}</td>}
              <td>
                <span style={{ fontSize: '12px', background: t.zona_name && t.zona_name !== 'Sin Zona' ? '#eff6ff' : 'transparent', color: t.zona_name && t.zona_name !== 'Sin Zona' ? '#1d4ed8' : '#94a3b8', padding: '2px 6px', borderRadius: '4px', fontWeight: t.zona_name && t.zona_name !== 'Sin Zona' ? '600' : 'normal' }}>
                  {t.zona_name ? (t.zona_name !== 'Sin Zona' ? `📍 ${t.zona_name}` : 'Sin Zona') : 'Sin Zona'}
                </span>
              </td>
              <td><span className={`badge ${t.estado === 'tramite_ejecutado' ? 'success' : t.estado === 'rechazado' ? 'danger' : 'warning'}`}>{t.estado}</span></td>
              <td>{new Date(t.created_at).toLocaleDateString('es-CO')}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    );
  };

  return (
    <div className="app-layout">
      <Sidebar userRole={user?.role} />

      <div className="main-content">
        <Navbar title="📊 Reportes" />

        <div className="page-content">
          {/* Selector de tipo de reporte */}
          <div className="reports-selector">
            <h2>Selecciona un reporte</h2>
            <div className="report-cards">
              {isSuperUserForReports && (
                <>
                  <div
                    className={`report-card ${reportType === 'cupls' ? 'active' : ''}`}
                    onClick={() => setReportType('cupls')}
                  >
                    <span className="icon">📋</span>
                    <span className="label">CUPL Registrados</span>
                  </div>
                  <div
                    className={`report-card ${reportType === 'pagos' ? 'active' : ''}`}
                    onClick={() => setReportType('pagos')}
                  >
                    <span className="icon">✓</span>
                    <span className="label">Pagos Realizados</span>
                  </div>
                </>
              )}
              {(!user?.transito_id && !user?.concesionario_id) && (
                <>
                  <div
                    className={`report-card ${reportType === 'caja' ? 'active' : ''}`}
                    onClick={() => setReportType('caja')}
                  >
                    <span className="icon">💰</span>
                    <span className="label">Movimientos Caja</span>
                  </div>
                  <div
                    className={`report-card ${reportType === 'auditoria' ? 'active' : ''}`}
                    onClick={() => setReportType('auditoria')}
                  >
                    <span className="icon">🔍</span>
                    <span className="label">Auditoría</span>
                  </div>
                </>
              )}
              <div
                className={`report-card ${reportType === 'tramites' ? 'active' : ''}`}
                onClick={() => setReportType('tramites')}
              >
                <span className="icon">📂</span>
                <span className="label">{(user?.transito_id || user?.concesionario_id) ? 'Reporte de Trámites' : 'Trámites Generales'}</span>
              </div>
            </div>
          </div>

          {/* Filtros */}
          <div className="filters-section">
            <div className="filter-group">
              <label>Rango de Fechas:</label>
              <div className="date-range">
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                />
                <span>hasta</span>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                />
              </div>
            </div>

            <div className="filter-group">
              <label>Tipo de Fecha:</label>
              <select
                value={filters.dateType}
                onChange={(e) => setFilters({ ...filters, dateType: e.target.value })}
              >
                <option value="reception">Fecha de Recepción</option>
                <option value="payment">Fecha de Pago</option>
              </select>
            </div>

            {reportType === 'tramites' && (
              <div className="filter-group">
                <label>Zona (Opcional):</label>
                <select
                  value={filters.zona_id}
                  onChange={(e) => setFilters({ ...filters, zona_id: e.target.value })}
                >
                  <option value="">Todas las Zonas</option>
                  {zonasFilterList.map(z => (
                    <option key={z.id} value={z.id}>📍 {z.name} ({z.concesionario_name})</option>
                  ))}
                </select>
              </div>
            )}

            {(reportType === 'cupls' || reportType === 'tramites') && (
              <div className="filter-group">
                <label>Estado:</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <option value="todos">Todos</option>
                  {reportType === 'cupls' ? (
                    <>
                      <option value="pagado">✓ Pagados</option>
                      <option value="pendiente">⏳ Pendientes</option>
                    </>
                  ) : (
                    <>
                      <option value="radicado">📝 Radicado</option>
                      <option value="pre_asignado">🚗 Pre-Asignado</option>
                      <option value="tramite_ejecutado">🎉 Ejecutado</option>
                      <option value="rechazado">❌ Rechazado</option>
                    </>
                  )}
                </select>
              </div>
            )}

            {reportType === 'caja' && (
              <div className="filter-group">
                <label>Tipo:</label>
                <select
                  value={filters.type}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                >
                  <option value="todos">Todos</option>
                  <option value="ingreso">📥 Ingresos</option>
                  <option value="egreso">📤 Egresos</option>
                </select>
              </div>
            )}

            {/* Botón exportar */}
            <button className="btn-export" onClick={exportToExcel}>
              📥 Descargar Excel
            </button>
          </div>

          {/* Contenido del reporte */}
          <div className="report-content">
            {renderReportTable()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
